import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isActivityTagNameInvalid, MAX_ACTIVITY_TAG_NAME_LENGTH } from '../activityTagName'

import { ActionModal, FormField, Modal, TextInput } from '@percent/lemonade'
import { type Err } from './types'
import { useCreateActivityTag } from './useCreateActivityTag'

export type CreateActivityTagModalProps = Readonly<{
  isOpen: boolean
  handleClose: () => void
  onSuccess: () => void
  onError: (error?: Err) => void
}>

export function CreateActivityTagModal({ isOpen, handleClose, onSuccess, onError }: CreateActivityTagModalProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')

  const { isLoading, createActivityTag } = useCreateActivityTag({
    onCreated: () => {
      setName('')
      onSuccess()
    },
    onError: (error: Err) => {
      onError(error)
    }
  })

  const onClose = () => {
    setName('')
    handleClose()
  }

  const trimmedName = name.trim()
  const isNameInvalid = isActivityTagNameInvalid(trimmedName)
  const isSubmitDisabled = !trimmedName || isNameInvalid

  const fieldStatus = isNameInvalid ? 'danger' : 'default'
  const statusMessage = isNameInvalid ? t('errorMessage.activityTagNameInvalid') : undefined

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ActionModal
        title={t('typography.createActivityTagTitle')}
        primaryButtonText={t('button.createActivityTag')}
        secondaryButtonText={t('button.cancel')}
        handleClose={onClose}
        handleSubmit={() => createActivityTag(trimmedName)}
        loading={isLoading}
        disabled={isSubmitDisabled}
        primaryBtnTestId="create-activity-tag-submit-button"
        secondaryBtnTestId="create-activity-tag-cancel-button"
      >
        <FormField
          label={t('typography.activityTagNameLabel')}
          necessity="required"
          status={fieldStatus}
          statusMessage={statusMessage}
        >
          <TextInput
            name="activityTagName"
            value={name}
            placeholder={t('typography.activityTagNamePlaceholder')}
            onChange={e => setName(e.target.value)}
            maxLength={MAX_ACTIVITY_TAG_NAME_LENGTH}
            data-testid="create-activity-tag-name-input"
          />
        </FormField>
      </ActionModal>
    </Modal>
  )
}
